import { useEffect, useState } from 'react';
import api from '../utils/api';
import { Activity, TrendingUp, AlertTriangle, CheckCircle2, Gauge, Factory, Clock, Package, RotateCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

function StatCard({ icon: Icon, label, value, suffix, color = 'text-white', sub }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <Icon size={18} className="text-gray-500" />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className={`text-3xl font-bold ${color}`}>
        {value}
        {suffix && <span className="text-lg text-gray-500 ml-1">{suffix}</span>}
      </p>
      {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div className="w-full bg-gray-800 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

const PERIOD_DAYS = { day: 1, week: 7, month: 30 };

export default function AnalyticsPage() {
  const [period, setPeriod] = useState('week');
  const [kpi, setKpi] = useState(null);
  const [throughput, setThroughput] = useState([]);
  const [utilization, setUtilization] = useState([]);
  const [failures, setFailures] = useState(null);
  const [defects, setDefects] = useState({ total: 0, byType: {}, byPrinter: {} });
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const days = PERIOD_DAYS[period];
    let cancelled = false;
    Promise.all([
      api.get(`/analytics/kpi?period=${period}`),
      api.get(`/analytics/throughput?days=${days}`),
      api.get(`/analytics/printer-utilization?days=${days}`),
      api.get(`/analytics/failure-summary?days=${days}`),
      api.get(`/qc/analytics/defects?days=${days}`).catch(() => ({ data: { total: 0, byType: {}, byPrinter: {} } })),
    ]).then(([k, t, u, f, d]) => {
      if (cancelled) return;
      setKpi(k.data);
      setThroughput(t.data);
      setUtilization(u.data);
      setFailures(f.data);
      setDefects(d.data);
    });
    return () => { cancelled = true; };
  }, [period]);

  const handleReset = async () => {
    if (!window.confirm('모든 생산 잡, 실패 감지, QC 기록이 삭제됩니다.\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?')) return;
    setResetting(true);
    try {
      await api.post('/analytics/reset-all');
      setKpi(null); setThroughput([]); setUtilization([]); setFailures(null);
      setDefects({ total: 0, byType: {}, byPrinter: {} });
      window.alert('데이터가 초기화되었습니다.');
    } catch (e) {
      window.alert('초기화 실패: ' + (e.response?.data?.message || e.message));
    } finally { setResetting(false); }
  };

  const maxThroughput = Math.max(1, ...throughput.map(t => t.total || 0));
  const defectEntries = Object.entries(defects.byType || {}).sort((a, b) => b[1] - a[1]);
  const maxDefect = Math.max(1, ...defectEntries.map(([, c]) => c));
  const oeeColor = (v) => v >= 70 ? 'text-green-400' : v >= 50 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div>
      <PageHeader icon={Activity} title="생산 KPI · 품질 분석">
          <div className="flex gap-1">
            {['day', 'week', 'month'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs ${period === p ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
              >{p === 'day' ? '오늘' : p === 'week' ? '주간' : '월간'}</button>
            ))}
          </div>
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-500 hover:text-red-400 rounded-lg text-xs disabled:opacity-40"
          >
            <RotateCcw size={12} /> 초기화
          </button>
        </PageHeader>

      {!kpi && <p className="text-gray-500">불러오는 중...</p>}

      {kpi && (
        <>
          {/* OEE 핵심 4카드 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              icon={Gauge}
              label="OEE (종합효율)"
              value={kpi.oee.overall}
              suffix="%"
              color={oeeColor(kpi.oee.overall)}
              sub="가동률 × 성능 × 품질"
            />
            <StatCard
              icon={Activity}
              label="가동률 (Availability)"
              value={kpi.oee.availability}
              suffix="%"
              sub={`가동 ${kpi.throughput.totalHours}h / 프린터 ${kpi.printerCount}대`}
            />
            <StatCard
              icon={TrendingUp}
              label="성능 (Performance)"
              value={kpi.oee.performance}
              suffix="%"
              sub={`완료 ${kpi.jobs.completed} / 총 ${kpi.jobs.total}`}
            />
            <StatCard
              icon={CheckCircle2}
              label="품질 (Quality)"
              value={kpi.oee.quality}
              suffix="%"
              color={kpi.oee.quality >= 95 ? 'text-green-400' : 'text-yellow-400'}
              sub="QC pass율"
            />
          </div>

          {/* 서브 카드 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Factory} label="총 잡" value={kpi.jobs.total} />
            <StatCard icon={CheckCircle2} label="완료" value={kpi.jobs.completed} color="text-green-400" />
            <StatCard icon={AlertTriangle} label="실패" value={kpi.jobs.failed} color="text-red-400" />
            <StatCard
              icon={Clock}
              label="평균 출력 시간"
              value={kpi.throughput.avgDurationMin}
              suffix="분"
            />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 일별 처리량 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-4">일별 처리량</h2>
          <div className="space-y-2">
            {throughput.map(t => (
              <div key={t.date} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16">{dayjs(t.date).format('MM/DD')}</span>
                <div className="flex-1">
                  <Bar pct={(t.total / maxThroughput) * 100} color="bg-brand" />
                </div>
                <span className="text-xs text-white w-20 text-right">
                  {t.completed}/{t.total}
                </span>
              </div>
            ))}
            {throughput.length === 0 && (
              <p className="text-gray-600 text-sm">데이터 없음</p>
            )}
          </div>
        </div>

        {/* 프린터별 가동률 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-4">프린터별 가동률</h2>
          <div className="space-y-2">
            {utilization.map(p => (
              <div key={p.printerId} className="flex items-center gap-3">
                <span className="text-xs text-white w-24 truncate">{p.printerName}</span>
                <div className="flex-1">
                  <Bar
                    pct={p.utilization}
                    color={p.utilization >= 70 ? 'bg-green-500' : p.utilization >= 40 ? 'bg-yellow-500' : 'bg-red-500'}
                  />
                </div>
                <span className="text-xs text-white w-12 text-right">{p.utilization}%</span>
              </div>
            ))}
            {utilization.length === 0 && (
              <p className="text-gray-600 text-sm">프린터 없음</p>
            )}
          </div>
        </div>

        {/* 실패 원인 분포 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-4">실패 원인 분포</h2>
          {failures && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>총 감지: {failures.total}건</span>
              </div>
              {Object.entries(failures.byType || {}).map(([type, count]) => (
                <div key={type} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-24 truncate">{type}</span>
                  <div className="flex-1">
                    <Bar pct={(count / Math.max(1, failures.total)) * 100} color="bg-orange-500" />
                  </div>
                  <span className="text-xs text-white w-10 text-right">{count}</span>
                </div>
              ))}
              {failures.total === 0 && <p className="text-gray-600 text-sm">실패 없음</p>}
            </div>
          )}
        </div>

        {/* QC 불량 유형 Top */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold text-sm mb-4">QC 불량 유형 (30일)</h2>
          <div className="space-y-2">
            <div className="text-xs text-gray-500 mb-2">총 {defects.total}건</div>
            {defectEntries.map(([type, count]) => (
              <div key={type} className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-24 truncate">{type}</span>
                <div className="flex-1">
                  <Bar pct={(count / maxDefect) * 100} color="bg-red-500" />
                </div>
                <span className="text-xs text-white w-10 text-right">{count}</span>
              </div>
            ))}
            {defectEntries.length === 0 && (
              <p className="text-gray-600 text-sm">불량 데이터 없음</p>
            )}
          </div>
        </div>
      </div>

      {/* 자재 / 필라멘트 사용 */}
      {kpi && (
        <div className="mt-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <Package size={18} className="text-gray-500" />
            <h2 className="text-white font-semibold text-sm">필라멘트 사용량 ({period})</h2>
          </div>
          <p className="text-3xl font-bold text-white">
            {kpi.throughput.totalFilamentGram}
            <span className="text-lg text-gray-500 ml-1">g</span>
          </p>
          <p className="text-xs text-gray-600 mt-1">
            총 출력 시간 {kpi.throughput.totalHours}h · 평균 {kpi.throughput.avgDurationMin}분/잡
          </p>
        </div>
      )}
    </div>
  );
}
